import { AutoCheckJobStatuses } from 'services/results/types'
import type { Result } from 'services/results/types'
import { formatDate } from 'utils/date'

export const getAutomatedChecksDate = (automatedChecks?: Result['automatedChecks']): string | undefined => {
  return automatedChecks?.triggeredAt
}

export const formatAutomatedChecksDate = (automatedChecks?: Result['automatedChecks']): string => {
  const date = getAutomatedChecksDate(automatedChecks)

  if (!date) return '-'
  return formatDate(date).replace(/:\d{2}$/, '')
}

export const getChecksResultFromAutoCheckJobStatus = (autoCheckJobStatus?: AutoCheckJobStatuses): string => {
  if (autoCheckJobStatus === AutoCheckJobStatuses.Passed) {
    return 'Airlock checks passed'
  }

  if (autoCheckJobStatus === AutoCheckJobStatuses.Failed) {
    return 'Airlock checks failed'
  }

  return 'Checks could not be completed'
}

export const getChecksResult = (params: {
  automatedChecks: Result['automatedChecks']
}): {
  label: string
  showStatusIcon: boolean
} => {
  const { automatedChecks } = params

  if (!automatedChecks) {
    return { label: '-', showStatusIcon: false }
  }

  const firstAutoCheckJobStatus = getAutomatedChecksJobs(automatedChecks)[0]?.status

  return {
    label: getChecksResultFromAutoCheckJobStatus(firstAutoCheckJobStatus),
    showStatusIcon: firstAutoCheckJobStatus !== AutoCheckJobStatuses.Passed
  }
}

type AutomatedChecksJobs = NonNullable<NonNullable<Result['automatedChecks']>['jobs']>

export const getAutomatedChecksJobs = (automatedChecks: Result['automatedChecks']): AutomatedChecksJobs => {
  return (automatedChecks?.jobs ?? []) as AutomatedChecksJobs
}

const parsePipelineVersionFromJobId = (jobId: string): string | undefined => {
  const versionFromSuffix = jobId.match(/(?:^|[/:_-])v(\d+)(?:$|[/:_-])/i)?.[1]
  if (versionFromSuffix) return `v${versionFromSuffix}`

  const trailingNumber = jobId.match(/(\d+)(?:$|[/:_-])/)
  if (trailingNumber?.[1]) return `v${trailingNumber[1]}`

  return undefined
}

export const getPipelineNameAndVersion = (automatedChecks: Result['automatedChecks']): string => {
  const firstJob = getAutomatedChecksJobs(automatedChecks)[0]
  if (!firstJob) return '-'

  const pipelineName = firstJob.workflowName?.trim() || 'Unknown pipeline'
  const version = firstJob.workflowVersion?.trim() || parsePipelineVersionFromJobId(firstJob.jobId)

  if (version) {
    return `${pipelineName} (${version})`
  }

  return pipelineName
}

export const getAutomatedChecksReportUrl = (automatedChecks: Result['automatedChecks']): string | undefined => {
  const firstJob = getAutomatedChecksJobs(automatedChecks)[0]
  if (!firstJob?.jobId) return undefined

  return firstJob.jobId
}
